import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, Loader2, ExternalLink, Heart, MessageSquare, Share2 } from "lucide-react";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function truncate(text, n = 140) {
  if (!text) return "";
  return text.length > n ? text.slice(0, n).trim() + "…" : text;
}

/**
 * Feed real (solo lectura) de Instagram o Facebook. Rejilla 3-cols en desktop,
 * 2-cols en tablet, 1-col en mobile. Caché backend 60s; force=1 al pulsar
 * "Actualizar" o cuando el padre llama `refreshRef.current()`.
 *
 * Props:
 * - platform: 'instagram' | 'facebook'
 * - connectionId?: string
 * - refreshRef?: React ref para exponer un método refresh() al padre
 */
export default function SocialFeed({ platform, connectionId, refreshRef }) {
  const [state, setState] = useState({ loading: true, ok: null, items: [], error: null, cached: false });
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async (force = false) => {
    if (mountedRef.current) setState((s) => ({ ...s, loading: true }));
    try {
      const params = new URLSearchParams();
      params.set("platform", platform);
      if (connectionId) params.set("connection_id", connectionId);
      if (force) params.set("force", "1");
      const r = await api.get(`/social/feed?${params.toString()}`);
      if (!mountedRef.current) return;
      setState({
        loading: false,
        ok: !!r.data.ok,
        items: r.data.items || [],
        error: r.data.error || null,
        cached: !!r.data.cached,
      });
    } catch (e) {
      if (!mountedRef.current) return;
      setState({
        loading: false,
        ok: false,
        items: [],
        error: e.response?.data?.detail || "No se pudo cargar el feed",
        cached: false,
      });
    }
  }, [platform, connectionId]);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    if (refreshRef) refreshRef.current = () => load(true);
    return () => { if (refreshRef) refreshRef.current = null; };
  }, [refreshRef, load]);

  const { loading, ok, items, error, cached } = state;

  return (
    <div className="space-y-4" data-testid={`social-feed-${platform}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h3 className="font-serif-display text-2xl text-brand-ink">Publicaciones reales</h3>
          {cached && (
            <span className="text-[10px] uppercase tracking-widest text-stone-400 border border-stone-200 rounded px-2 py-0.5">
              caché · 60s
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load(true)}
          disabled={loading}
          data-testid={`feed-refresh-${platform}`}
          className="border-brand-emerald text-brand-emerald-dark hover:bg-brand-emerald-soft"
        >
          {loading
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <RefreshCw className="h-4 w-4 mr-2" />}
          Actualizar
        </Button>
      </div>

      {loading && items.length === 0 ? (
        <div className="card-elevated p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
        </div>
      ) : !ok && error ? (
        <div className="card-elevated p-6 border-brand-gold/40 bg-brand-gold-soft/30" data-testid={`feed-error-${platform}`}>
          <div className="text-sm text-brand-ink">
            <strong>No se pudo cargar el feed real:</strong> {error}
          </div>
          <div className="text-xs text-stone-500 mt-1">
            Vincula la cuenta a Composio en <a href="/conectividad" className="underline text-brand-emerald-dark">Conectividad</a> y añade
            {platform === "facebook" ? " el page_id" : " el ig_user_id"} correspondiente.
          </div>
        </div>
      ) : items.length === 0 ? (
        <div className="card-elevated p-12 text-center text-stone-500">
          Sin publicaciones todavía en esta cuenta.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`feed-grid-${platform}`}>
          {items.map((p) => (
            <a
              key={p.id}
              href={p.permalink_url || "#"}
              target="_blank"
              rel="noreferrer"
              className="card-elevated overflow-hidden hover:border-brand-emerald/50 transition-all group"
              data-testid={`feed-item-${p.id}`}
            >
              {p.media_url ? (
                <div className="aspect-square bg-stone-100 overflow-hidden">
                  <img
                    src={p.media_url}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
              ) : (
                <div className="aspect-square bg-brand-emerald-soft/40 flex items-center justify-center text-brand-emerald-dark text-xs">
                  Sin imagen
                </div>
              )}
              <div className="p-4 space-y-2">
                <p className="text-sm text-brand-ink line-clamp-3 min-h-[3.5rem]">
                  {truncate(p.message, 180) || <span className="text-stone-400 italic">Sin texto</span>}
                </p>
                <div className="flex items-center justify-between text-xs text-stone-500 pt-1 border-t border-stone-100">
                  <span>{fmt(p.created_time)}</span>
                  {p.permalink_url && (
                    <ExternalLink className="h-3.5 w-3.5 text-brand-emerald-dark opacity-60 group-hover:opacity-100" />
                  )}
                </div>
                {(p.reactions != null || p.comments != null || p.shares != null) && (
                  <div className="flex items-center gap-3 text-xs text-stone-600 pt-1">
                    {p.reactions != null && (
                      <span className="inline-flex items-center gap-1">
                        <Heart className="h-3 w-3" /> {p.reactions}
                      </span>
                    )}
                    {p.comments != null && (
                      <span className="inline-flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" /> {p.comments}
                      </span>
                    )}
                    {p.shares != null && (
                      <span className="inline-flex items-center gap-1">
                        <Share2 className="h-3 w-3" /> {p.shares}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
